import React, { useState, useEffect } from 'react';
import { XMarkIcon, RectangleStackIcon, SparklesIcon, CheckIcon, PlusIcon, TrashIcon, PencilSquareIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { deckService } from '../services/deckService';
import matchService from '../services/matchService';
import toast from 'react-hot-toast';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';

interface Deck {
  _id: string;
  name: string;
  description: string;
  cardCount: number;
}

interface CreateMatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'deck-based' | 'custom' | 'ai-generated' | null;
  onMatchCreated?: () => void;
  onUpgradePrompt?: () => void;
}

const CreateMatchModal: React.FC<CreateMatchModalProps> = ({ isOpen, onClose, type, onMatchCreated, onUpgradePrompt }) => {
  const navigate = useNavigate();
  const { checkLimit } = useSubscription();
  const [step, setStep] = useState(1);
  const [selectedDeck, setSelectedDeck] = useState<string | null>(null);
  const [matchTitle, setMatchTitle] = useState('');
  const [matchDescription, setMatchDescription] = useState('');
  const [pairsCount, setPairsCount] = useState(8);
  const [customPairs, setCustomPairs] = useState<{
    front: string;
    back: string;
  }[]>([]);
  const [aiTopic, setAiTopic] = useState('');
  const [aiDifficulty, setAiDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [aiLanguage, setAiLanguage] = useState<'portuguese' | 'english' | 'spanish'>('portuguese');
  const [decks, setDecks] = useState<Deck[]>([]);
  const [loading, setLoading] = useState(false);
  const [matchCount, setMatchCount] = useState(0);

  useEffect(() => {
    const fetchDecks = async () => {
      if (isOpen && type === 'deck-based') {
        try {
          const userDecks = await deckService.getDecks();
          setDecks(userDecks.map(deck => ({
            _id: deck._id,
            name: deck.name,
            description: deck.description || '',
            cardCount: deck.cardCount
          })));
        } catch (error) {
          console.error('Error fetching decks:', error);
          toast.error('Erro ao carregar decks');
        }
      }
    };

    const loadMatchCount = async () => {
      if (isOpen) {
        try {
          // TODO: Replace with actual API call when backend is ready
          // const matches = await matchService.getMatchGames();
          // setMatchCount(matches.length);
          setMatchCount(0);
        } catch (error) {
          console.error('Error loading match count:', error);
        }
      }
    };

    fetchDecks();
    loadMatchCount();
  }, [isOpen, type]);

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedDeck(null);
      setMatchTitle('');
      setMatchDescription('');
      setPairsCount(8);
      setCustomPairs([]);
      setAiTopic('');
      setAiDifficulty('medium');
      setAiLanguage('portuguese');
    }
  }, [isOpen, type]);

  const handleSubmit = async () => {
    // Check quiz limit temporarily until match is added to subscription
    if (!checkLimit('quiz', matchCount)) {
      if (onUpgradePrompt) onUpgradePrompt();
      return;
    }

    setLoading(true);
    
    try {
      const matchData = {
        title: matchTitle,
        description: matchDescription,
        type: type!,
        ...(type === 'deck-based' && {
          deckId: selectedDeck!,
          pairsCount
        }),
        ...(type === 'custom' && {
          pairs: customPairs
        }),
        ...(type === 'ai-generated' && {
          topic: aiTopic,
          difficulty: aiDifficulty,
          language: aiLanguage,
          pairsCount
        })
      };

      console.log('Creating match game:', matchData);
      await matchService.createMatch(matchData);
      
      toast.success('Jogo Match criado com sucesso!');
      onMatchCreated?.();
      onClose();
    } catch (error: any) {
      console.error('Error creating match game:', error);
      toast.error(error.response?.data?.message || 'Erro ao criar jogo Match');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchMatches = () => {
    onClose();
    navigate('/search?type=matches');
  };

  const canProceedToStep2 = () => {
    if (type === 'deck-based') {
      return selectedDeck !== null;
    } else if (type === 'custom') {
      return customPairs.length >= 1 && 
             customPairs.every(p => 
               p.front.trim() !== '' && 
               p.back.trim() !== ''
             );
    } else if (type === 'ai-generated') {
      return aiTopic.trim() !== '';
    }
    return false;
  };

  const canSubmit = () => {
    if (type === 'deck-based') {
      return matchTitle.trim() !== '' && selectedDeck !== null;
    } else if (type === 'custom') {
      return matchTitle.trim() !== '' && customPairs.length >= 1 && 
             customPairs.every(p => 
               p.front.trim() !== '' && 
               p.back.trim() !== ''
             );
    } else if (type === 'ai-generated') {
      return matchTitle.trim() !== '' && aiTopic.trim() !== '';
    }
    return false;
  };

  const addPair = () => {
    setCustomPairs([...customPairs, {
      front: '',
      back: ''
    }]);
  };

  const updatePair = (index: number, field: 'front' | 'back', value: string) => {
    const updatedPairs = [...customPairs];
    updatedPairs[index][field] = value;
    setCustomPairs(updatedPairs);
  };

  const removePair = (index: number) => {
    setCustomPairs(customPairs.filter((_, i) => i !== index));
  };

  if (!isOpen) return null;

  const getModalTitle = () => {
    switch (type) {
      case 'deck-based': return 'Criar Match do Deck';
      case 'custom': return 'Criar Match Personalizado';
      case 'ai-generated': return 'Criar Match com IA';
      default: return 'Criar Match';
    }
  };

  const getModalDescription = () => {
    switch (type) {
      case 'deck-based': return 'Gere pares automaticamente baseados em um deck';
      case 'custom': return 'Crie seus próprios pares personalizados';
      case 'ai-generated': return 'Deixe a IA gerar pares sobre qualquer tema';
      default: return '';
    }
  };

  const getModalIcon = () => {
    switch (type) {
      case 'deck-based': return <RectangleStackIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />;
      case 'custom': return <PencilSquareIcon className="h-6 w-6 text-pink-600 dark:text-pink-400" />;
      case 'ai-generated': return <SparklesIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />;
      default: return null;
    }
  };

  const getModalColor = () => {
    switch (type) {
      case 'deck-based': return 'blue';
      case 'custom': return 'pink';
      case 'ai-generated': return 'purple';
      default: return 'blue';
    }
  };

  const color = getModalColor();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className={`p-2 rounded-lg mr-3 bg-${color}-100 dark:bg-${color}-900/30`}>
              {getModalIcon()}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {getModalTitle()}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {getModalDescription()}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
              step >= 1 
                ? `bg-${color}-600 border-${color}-600 text-white` 
                : 'border-gray-300 dark:border-gray-600 text-gray-400'
            }`}>
              {step > 1 ? <CheckIcon className="h-4 w-4" /> : '1'}
            </div>
            <div className={`flex-1 h-0.5 mx-4 ${
              step > 1 ? `bg-${color}-600` : 'bg-gray-300 dark:bg-gray-600'
            }`} />
            <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
              step >= 2 
                ? `bg-${color}-600 border-${color}-600 text-white` 
                : 'border-gray-300 dark:border-gray-600 text-gray-400'
            }`}>
              2
            </div>
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {type === 'deck-based' && 'Selecionar Deck'}
              {type === 'custom' && 'Criar Pares'}
              {type === 'ai-generated' && 'Configurar IA'}
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Detalhes do Match
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 1 && (
            <div>
              {/* Deck-based type */}
              {type === 'deck-based' && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Selecione um Deck
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Escolha o deck que será usado para gerar os pares do jogo Match automaticamente.
                  </p>
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {decks.map((deck) => (
                      <label
                        key={deck._id}
                        className={`block p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                          selectedDeck === deck._id
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'
                        }`}
                      >
                        <input
                          type="radio"
                          name="deck"
                          value={deck._id}
                          checked={selectedDeck === deck._id}
                          onChange={(e) => setSelectedDeck(e.target.value)}
                          className="sr-only"
                        />
                        <div className="flex items-center justify-between">
                          <div>
                            <h5 className="font-medium text-gray-900 dark:text-gray-100">
                              {deck.name}
                            </h5>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {deck.description}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                              {deck.cardCount} cartas
                            </span>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom type */}
              {type === 'custom' && (
                <div>
                  <div className="text-center py-8 mb-6">
                    <PencilSquareIcon className="mx-auto h-16 w-16 text-pink-400 mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Match Personalizado
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      Crie seus próprios pares personalizados para o jogo Match.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h5 className="text-md font-medium text-gray-900 dark:text-gray-100">
                        Pares ({customPairs.length})
                      </h5>
                      <button
                        onClick={addPair}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-pink-600 hover:text-pink-700 dark:text-pink-400 dark:hover:text-pink-300"
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Adicionar Par
                      </button>
                    </div>

                    {customPairs.length === 0 ? (
                      <div className="text-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                        <p className="text-gray-500 dark:text-gray-400 mb-4">
                          Nenhum par adicionado ainda
                        </p>
                        <button
                          onClick={addPair}
                          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-pink-600 hover:bg-pink-700 rounded-lg"
                        >
                          <PlusIcon className="h-4 w-4 mr-2" />
                          Adicionar Primeiro Par
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4 max-h-96 overflow-y-auto">
                        {customPairs.map((pair, index) => (
                          <div key={index} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg space-y-4">
                            <div className="flex items-center justify-between">
                              <h6 className="font-medium text-gray-900 dark:text-gray-100">
                                Par {index + 1}
                              </h6>
                              <button
                                onClick={() => removePair(index)}
                                className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  Frente
                                </label>
                                <input
                                  type="text"
                                  value={pair.front}
                                  onChange={(e) => updatePair(index, 'front', e.target.value)}
                                  placeholder="Ex: Hello"
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 dark:bg-gray-700 dark:text-gray-100"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  Verso
                                </label>
                                <input
                                  type="text"
                                  value={pair.back}
                                  onChange={(e) => updatePair(index, 'back', e.target.value)}
                                  placeholder="Ex: Olá"
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 dark:bg-gray-700 dark:text-gray-100"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* AI-generated type */}
              {type === 'ai-generated' && (
                <div>
                  <div className="text-center py-8 mb-6">
                    <SparklesIcon className="mx-auto h-16 w-16 text-purple-400 mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Match Gerado por IA
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      A IA criará pares relacionados automaticamente sobre qualquer tema.
                    </p>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tema ou Tópico
                      </label>
                      <input
                        type="text"
                        value={aiTopic}
                        onChange={(e) => setAiTopic(e.target.value)}
                        placeholder="Ex: Vocabulário de inglês para programadores"
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-gray-100"
                      />
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        Descreva o tema sobre o qual você quer criar o jogo Match
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Nível de Dificuldade
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {(['easy', 'medium', 'hard'] as const).map((difficulty) => (
                          <button
                            key={difficulty}
                            type="button"
                            onClick={() => setAiDifficulty(difficulty)}
                            className={`px-4 py-3 border-2 rounded-lg text-center transition-all duration-200 ${
                              aiDifficulty === difficulty
                                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                                : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-purple-300'
                            }`}
                          >
                            <div className="font-medium">
                              {difficulty === 'easy' && '🟢 Fácil'}
                              {difficulty === 'medium' && '🟡 Médio'}
                              {difficulty === 'hard' && '🔴 Difícil'}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Idioma
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {([
                          { value: 'portuguese', label: 'Português', flag: '🇧🇷' },
                          { value: 'english', label: 'English', flag: '🇺🇸' },
                          { value: 'spanish', label: 'Español', flag: '🇪🇸' }
                        ] as const).map((lang) => (
                          <button
                            key={lang.value}
                            type="button"
                            onClick={() => setAiLanguage(lang.value)}
                            className={`px-4 py-3 border-2 rounded-lg text-center transition-all duration-200 ${
                              aiLanguage === lang.value
                                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                                : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-purple-300'
                            }`}
                          >
                            <div className="font-medium">
                              {lang.flag} {lang.label}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Número de Pares: {pairsCount}
                      </label>
                      <input
                        type="range"
                        min="4"
                        max="20"
                        step="2"
                        value={pairsCount}
                        onChange={(e) => setPairsCount(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                      />
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span>4 pares</span>
                        <span>20 pares</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Search Matches Option - Only show when not in a specific type */}
              {!type && (
                <div className="mb-6">
                  <button
                    onClick={handleSearchMatches}
                    className="w-full p-4 sm:p-6 border-2 border-dashed border-orange-300 dark:border-orange-600 rounded-lg hover:border-orange-500 dark:hover:border-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all duration-200 group"
                  >
                    <div className="flex items-center space-x-3 sm:space-x-4">
                      <div className="flex-shrink-0">
                        <MagnifyingGlassIcon className="h-6 w-6 sm:h-8 sm:w-8 text-orange-600 dark:text-orange-400 group-hover:text-orange-700 dark:group-hover:text-orange-300" />
                      </div>
                      <div className="flex-1 text-left">
                        <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-orange-700 dark:group-hover:text-orange-300">
                          Pesquisar Matches
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                          Explore e copie jogos Match compartilhados criados por especialistas
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div>
              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-6">
                Detalhes do Match
              </h4>
              
              {type === 'custom' && (
                <div className="mb-6 p-4 bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded-lg">
                  <p className="text-sm text-pink-700 dark:text-pink-300">
                    <PencilSquareIcon className="h-4 w-4 inline mr-1" />
                    Seu jogo Match personalizado tem {customPairs.length} par{customPairs.length !== 1 ? 'es' : ''}.
                  </p>
                </div>
              )}

              {type === 'ai-generated' && (
                <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                  <p className="text-sm text-purple-700 dark:text-purple-300">
                    <SparklesIcon className="h-4 w-4 inline mr-1" />
                    A IA gerará {pairsCount} pares sobre "{aiTopic}" no nível {aiDifficulty === 'easy' ? 'fácil' : aiDifficulty === 'medium' ? 'médio' : 'difícil'} em {aiLanguage === 'portuguese' ? 'Português' : aiLanguage === 'spanish' ? 'Español' : 'English'}.
                  </p>
                </div>
              )}
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Título do Match *
                  </label>
                  <input
                    type="text"
                    value={matchTitle}
                    onChange={(e) => setMatchTitle(e.target.value)}
                    placeholder="Ex: Match de Vocabulário em Inglês"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Descrição (Opcional)
                  </label>
                  <textarea
                    value={matchDescription}
                    onChange={(e) => setMatchDescription(e.target.value)}
                    placeholder="Descreva o objetivo deste jogo Match..."
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                {type === 'deck-based' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Número de Pares: {pairsCount}
                    </label>
                    <input
                      type="range"
                      min="4"
                      max="20"
                      step="2"
                      value={pairsCount}
                      onChange={(e) => setPairsCount(parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                    />
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <span>4 pares</span>
                      <span>20 pares</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-row gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          {step === 1 ? (
            <>
              <button
                onClick={onClose}
                className="flex-1 px-5 py-3.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all text-base font-semibold"
              >
                ← Cancelar
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={!canProceedToStep2()}
                className={`flex-1 px-5 py-3.5 rounded-xl transition-all text-base font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                  canProceedToStep2()
                    ? `bg-gradient-to-r from-${color}-600 to-${color}-700 hover:from-${color}-700 hover:to-${color}-800 text-white shadow-${color}-500/30`
                    : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                }`}
              >
                Continuar →
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setStep(1)}
                className="flex-1 px-5 py-3.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all text-base font-semibold"
              >
                ← Voltar
              </button>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit() || loading}
                className={`flex-1 px-5 py-3.5 rounded-xl transition-all text-base font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                  canSubmit() && !loading
                    ? `bg-gradient-to-r from-${color}-600 to-indigo-600 hover:from-${color}-700 hover:to-indigo-700 text-white shadow-${color}-500/30`
                    : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    <span>Criando...</span>
                  </div>
                ) : (
                  'Criar Match'
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateMatchModal;